/** 한 번에 표시·선택 가능한 페이지 번호 최대 개수 */
export const MAX_VISIBLE_PAGES = 10;

/** 모달 등 좁은 영역용 페이지 번호 최대 개수 */
export const MAX_VISIBLE_PAGES_SM = 5;

export type PaginationSize = 'default' | 'sm';

/**
 * RDS Pagination 아이템 공통 스타일 (default: 48px 원형).
 * @see https://www.figma.com/design/wcgji99mZRPIytCZDjwqxe/RDS-v1.0?node-id=235-6281
 */
export const paginationItemClassName =
  'inline-flex size-12 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-base font-normal leading-6 tracking-tight text-ruda-text-primary shadow-none transition-colors hover:bg-ruda-grey-100 focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#aeb6fb] disabled:pointer-events-none disabled:text-[#d2d5db] disabled:hover:bg-transparent';

/** 모달·좁은 영역용 — 32px 원형 */
export const paginationItemSmClassName =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-xs font-normal leading-4 tracking-tight text-ruda-text-primary shadow-none transition-colors hover:bg-ruda-grey-100 focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#aeb6fb] disabled:pointer-events-none disabled:text-[#d2d5db] disabled:hover:bg-transparent';

export const paginationItemClassNameBySize: Record<PaginationSize, string> = {
  default: paginationItemClassName,
  sm: paginationItemSmClassName,
};

/** Selected — Black 원 + white 텍스트 */
export const paginationItemSelectedClassName =
  'bg-[#131927] text-white hover:bg-[#131927] focus-visible:shadow-[0_0_0_2px_#aeb6fb] pointer-events-none';

/**
 * 페이지 번호 자릿수에 맞춰 글자 크기를 조정한다.
 * RDS 기본은 16px / 48px 원형. sm은 더 작은 스케일.
 */
export const getPageNumberButtonClass = (
  pageNum: number,
  size: PaginationSize = 'default',
): string => {
  const digits = String(pageNum).length;
  const base = 'tabular-nums';

  if (size === 'sm') {
    if (digits <= 2) return `${base} text-xs`;
    if (digits === 3) return `${base} text-[10px]`;
    return `${base} text-[9px]`;
  }

  if (digits <= 2) {
    return `${base} text-base`;
  }
  if (digits === 3) {
    return `${base} text-sm`;
  }
  if (digits === 4) {
    return `${base} text-xs`;
  }
  return `${base} text-[10px]`;
};

/**
 * 현재 페이지를 중심으로 최대 N개의 페이지 번호를 반환한다.
 * 전체 페이지가 maxVisible 이하면 모두 표시한다.
 */
export const getVisiblePages = (
  currentPage: number,
  totalPages: number,
  maxVisible = MAX_VISIBLE_PAGES,
): number[] => {
  if (totalPages <= 0) return [1];
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = start + maxVisible - 1;

  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - maxVisible + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

export const getMaxVisiblePages = (size: PaginationSize): number =>
  size === 'sm' ? MAX_VISIBLE_PAGES_SM : MAX_VISIBLE_PAGES;
